import type { InstrumentId } from "@trader/shared/MarketData"
import {
  type AccountId,
  type AccountSnapshot,
  type AccountSummary,
  type Fill,
  type FillsPage,
  isOpenOrderStatus,
  type Order,
  type OrderId,
  type OrdersPage,
  type Position,
  type PositionMark,
  type Product,
  type Runner
} from "@trader/shared/Money"
import type { AccountTopicStatus, AccountUpdate } from "@trader/shared/topics/Account"
import type { SubscribeFailedReason } from "@trader/shared/WsProtocol"
import { BigDecimal } from "effect"

// The selected paper account as the UI sees it (docs/design/0005, Frontend):
// a plain reducer over the account topic's Snapshot / Update / Status, plus the
// order history and fills read over HTTP. No React and no IO here; the store at
// the bottom takes its feed as a dependency, so all of it runs in unit tests.
//
// Updates apply strictly in seq order. live.ts already resubscribes on gaps; the
// reducer checks again and reports a gap so the store resubscribes too (a fresh
// snapshot replaces everything that can change).

export type LoadStatus = "idle" | "loading" | "ready" | "failed"

export interface PageState {
  /** Pass as `before` for the next (older) page; null when there is none. */
  readonly nextBefore: number | null
  readonly status: LoadStatus
  /** A page arrived at least once (so a null `nextBefore` means "everything is loaded"). */
  readonly loaded: boolean
}

export interface AccountState {
  readonly accountId: AccountId
  /** connecting: waiting for the first snapshot. failed: the server refused the subscription. */
  readonly phase: "connecting" | "live" | "failed"
  readonly error: SubscribeFailedReason | null
  /** The topic's update counter (the last update applied). */
  readonly seq: number
  readonly summary: AccountSummary | null
  /** Open positions first (by instrument), then flat ones; marks from the latest status applied. */
  readonly positions: ReadonlyArray<Position>
  /** Orders that can still fill or be cancelled, newest first. */
  readonly openOrders: ReadonlyArray<Order>
  /** Terminal orders, newest first. */
  readonly orderHistory: ReadonlyArray<Order>
  /** Newest first. */
  readonly fills: ReadonlyArray<Fill>
  readonly runners: ReadonlyArray<Runner>
  /** The newest fill that arrived live (not from a history page), for notices. */
  readonly lastLiveFill: Fill | null
  readonly ordersPage: PageState
  readonly fillsPage: PageState
  // Indexes the lists above are built from.
  readonly orderById: ReadonlyMap<OrderId, Order>
  readonly fillById: ReadonlyMap<string, Fill>
  readonly positionByKey: ReadonlyMap<string, Position>
  readonly marks: ReadonlyMap<string, PositionMark>
}

export const positionKey = (instrumentId: InstrumentId, product: Product): string => `${instrumentId}|${product}`

const IDLE_PAGE: PageState = { nextBefore: null, status: "idle", loaded: false }

export const initialAccountState = (accountId: AccountId): AccountState => ({
  accountId,
  phase: "connecting",
  error: null,
  seq: 0,
  summary: null,
  positions: [],
  openOrders: [],
  orderHistory: [],
  fills: [],
  runners: [],
  lastLiveFill: null,
  ordersPage: IDLE_PAGE,
  fillsPage: IDLE_PAGE,
  orderById: new Map(),
  fillById: new Map(),
  positionByKey: new Map(),
  marks: new Map()
})

// --- Derived lists ------------------------------------------------------------------------

const bySeqDesc = <T extends { readonly seq: number }>(a: T, b: T) => b.seq - a.seq

const withOrders = (state: AccountState, orderById: ReadonlyMap<OrderId, Order>): AccountState => {
  const all = [...orderById.values()].sort(bySeqDesc)
  return {
    ...state,
    orderById,
    openOrders: all.filter((order) => isOpenOrderStatus(order.status)),
    orderHistory: all.filter((order) => !isOpenOrderStatus(order.status))
  }
}

const withFills = (state: AccountState, fillById: ReadonlyMap<string, Fill>): AccountState => ({
  ...state,
  fillById,
  fills: [...fillById.values()].sort(bySeqDesc)
})

/** A position with the latest mark overlaid (the status carries what changes every bar). */
const marked = (position: Position, mark: PositionMark | undefined): Position =>
  mark === undefined ? position : { ...position, mark: mark.mark, markTime: mark.markTime, unrealizedPnl: mark.unrealizedPnl }

const withPositions = (
  state: AccountState,
  positionByKey: ReadonlyMap<string, Position>,
  marks: ReadonlyMap<string, PositionMark>
): AccountState => {
  const list = [...positionByKey.entries()].map(([key, position]) => marked(position, marks.get(key)))
  const open = (p: Position) => (BigDecimal.isZero(p.qty) ? 1 : 0)
  list.sort((a, b) => open(a) - open(b) || a.instrumentId.localeCompare(b.instrumentId) || a.product.localeCompare(b.product))
  return { ...state, positionByKey, marks, positions: list }
}

// --- Topic frames -------------------------------------------------------------------------

/**
 * A fresh snapshot replaces everything that can change. Terminal orders and
 * fills never change, so the ones already loaded are kept (no flicker while the
 * history reloads); an open order the snapshot no longer lists became terminal
 * while frames were missed, so it is dropped until the history reload brings it back.
 */
export const applySnapshot = (state: AccountState, snapshot: AccountSnapshot, seq: number): AccountState => {
  const orderById = new Map<OrderId, Order>()
  for (const order of state.orderById.values()) {
    if (!isOpenOrderStatus(order.status)) orderById.set(order.id, order)
  }
  for (const order of snapshot.openOrders) orderById.set(order.id, order)
  const positionByKey = new Map(snapshot.positions.map((p) => [positionKey(p.instrumentId, p.product), p] as const))
  const next: AccountState = {
    ...state,
    phase: "live",
    error: null,
    seq,
    summary: snapshot.summary,
    runners: snapshot.runners
  }
  return withPositions(withOrders(next, orderById), positionByKey, new Map())
}

export interface UpdateResult {
  readonly state: AccountState
  /** The update doesn't follow the last one: resubscribe for a fresh snapshot. */
  readonly gap: boolean
}

export const applyUpdate = (state: AccountState, update: AccountUpdate, seq: number): UpdateResult => {
  // Before the first snapshot nothing can apply; a repeat is already in.
  if (state.phase !== "live" || seq <= state.seq) return { state, gap: false }
  if (seq !== state.seq + 1) return { state, gap: true }
  const next = { ...state, seq }
  switch (update._tag) {
    case "OrderUpserted": {
      const orderById = new Map(state.orderById)
      orderById.set(update.order.id, update.order)
      return { state: withOrders(next, orderById), gap: false }
    }
    case "FillAdded": {
      const fillById = new Map(state.fillById)
      fillById.set(update.fill.id, update.fill)
      return { state: { ...withFills(next, fillById), lastLiveFill: update.fill }, gap: false }
    }
    case "PositionUpserted": {
      const key = positionKey(update.position.instrumentId, update.position.product)
      const positionByKey = new Map(state.positionByKey)
      positionByKey.set(key, update.position)
      // The update carries its own mark and P&L; an older status mark would be for the old quantity.
      const marks = new Map(state.marks)
      marks.delete(key)
      return { state: withPositions(next, positionByKey, marks), gap: false }
    }
    case "RunnerUpserted": {
      const runner = update.runner
      const exists = state.runners.some((r) => r.id === runner.id)
      const runners = exists ? state.runners.map((r) => (r.id === runner.id ? runner : r)) : [...state.runners, runner]
      return { state: { ...next, runners }, gap: false }
    }
  }
}

/** Latest-wins: the summary and the marks replace the previous ones. */
export const applyStatus = (state: AccountState, status: AccountTopicStatus): AccountState => {
  if (state.phase !== "live") return state
  switch (status._tag) {
    case "SummaryChanged": {
      const marks = new Map(status.marks.map((mark) => [positionKey(mark.instrumentId, mark.product), mark] as const))
      return withPositions({ ...state, summary: status.summary }, state.positionByKey, marks)
    }
  }
}

export const applySubscribeFailed = (state: AccountState, error: SubscribeFailedReason): AccountState => ({
  ...state,
  phase: "failed",
  error
})

// --- History pages (HTTP) -------------------------------------------------------------------

/**
 * The cursor after a page arrives. An older page moves it back; a refresh of the
 * newest page keeps whatever was already paged further back.
 */
const nextCursor = (previous: PageState, pageNext: number | null, older: boolean): number | null => {
  if (older || !previous.loaded) return pageNext
  if (previous.nextBefore === null || pageNext === null) return null
  return Math.min(previous.nextBefore, pageNext)
}

/**
 * Merges a page of orders. An order already known came from the snapshot or a
 * later update, which is at least as new as the page, so it wins.
 */
export const mergeOrdersPage = (state: AccountState, page: OrdersPage, older: boolean): AccountState => {
  const orderById = new Map(state.orderById)
  for (const order of page.orders) if (!orderById.has(order.id)) orderById.set(order.id, order)
  return {
    ...withOrders(state, orderById),
    ordersPage: { nextBefore: nextCursor(state.ordersPage, page.nextBefore, older), status: "ready", loaded: true }
  }
}

/** Merges a page of fills (fills never change, so ones already known are skipped). */
export const mergeFillsPage = (state: AccountState, page: FillsPage, older: boolean): AccountState => {
  const fillById = new Map(state.fillById)
  for (const fill of page.fills) if (!fillById.has(fill.id)) fillById.set(fill.id, fill)
  return {
    ...withFills(state, fillById),
    fillsPage: { nextBefore: nextCursor(state.fillsPage, page.nextBefore, older), status: "ready", loaded: true }
  }
}

// --- Queries for the chart --------------------------------------------------------------------

export const openOrdersOn = (state: AccountState, instrumentId: InstrumentId): ReadonlyArray<Order> =>
  state.openOrders.filter((order) => order.instrumentId === instrumentId)

export const openPositionsOn = (state: AccountState, instrumentId: InstrumentId): ReadonlyArray<Position> =>
  state.positions.filter((p) => p.instrumentId === instrumentId && !BigDecimal.isZero(p.qty))

export const fillsOn = (state: AccountState, instrumentId: InstrumentId): ReadonlyArray<Fill> =>
  state.fills.filter((fill) => fill.instrumentId === instrumentId)

// --- The store ----------------------------------------------------------------------------------

export interface AccountHandlers {
  readonly onSnapshot: (snapshot: AccountSnapshot, seq: number) => void
  readonly onUpdate: (update: AccountUpdate, seq: number) => void
  readonly onStatus: (status: AccountTopicStatus) => void
  readonly onError: (error: SubscribeFailedReason) => void
}

/** Where the store gets its data: the account topic and the history endpoints. */
export interface AccountFeed {
  readonly subscribe: (accountId: AccountId, handlers: AccountHandlers) => () => void
  /** Terminal orders, newest first; `before` pages back. */
  readonly loadOrders: (accountId: AccountId, before: number | undefined) => Promise<OrdersPage>
  readonly loadFills: (accountId: AccountId, before: number | undefined) => Promise<FillsPage>
}

export interface AccountStore {
  /** Follows one account (null: none). Selecting the same account again does nothing. */
  readonly select: (accountId: AccountId | null) => void
  readonly getState: () => AccountState | null
  readonly subscribe: (listener: () => void) => () => void
  readonly loadOlderOrders: () => void
  readonly loadOlderFills: () => void
  /** Reloads the newest page of orders and fills (after a failed load). */
  readonly refreshHistory: () => void
}

/**
 * Keeps the selected account's state. `batch` coalesces listener calls (one
 * per animation frame in the app; synchronous in tests).
 */
export const createAccountStore = (feed: AccountFeed, batch: (flush: () => void) => () => void): AccountStore => {
  let state: AccountState | null = null
  let stop: (() => void) | null = null
  /** Bumped on every selection: late responses for another account are dropped. */
  let generation = 0
  const listeners = new Set<() => void>()
  const notify = batch(() => {
    for (const listener of listeners) listener()
  })

  const set = (next: AccountState) => {
    if (next === state) return
    state = next
    notify()
  }

  const loadPage = (kind: "orders" | "fills", appendOlder: boolean) => {
    const current = state
    if (current === null) return
    const page = kind === "orders" ? current.ordersPage : current.fillsPage
    if (appendOlder && (page.nextBefore === null || page.status === "loading")) return
    const before = appendOlder ? page.nextBefore ?? undefined : undefined
    const mine = generation
    const loading: PageState = { ...page, status: "loading" }
    set(kind === "orders" ? { ...current, ordersPage: loading } : { ...current, fillsPage: loading })
    const done = (apply: (s: AccountState) => AccountState) => {
      if (mine === generation && state !== null) set(apply(state))
    }
    const failed = (s: AccountState): AccountState =>
      kind === "orders" ? { ...s, ordersPage: { ...s.ordersPage, status: "failed" } } : { ...s, fillsPage: { ...s.fillsPage, status: "failed" } }
    if (kind === "orders") {
      feed.loadOrders(current.accountId, before).then(
        (result) => done((s) => mergeOrdersPage(s, result, appendOlder)),
        () => done(failed)
      )
    } else {
      feed.loadFills(current.accountId, before).then(
        (result) => done((s) => mergeFillsPage(s, result, appendOlder)),
        () => done(failed)
      )
    }
  }

  const listen = (accountId: AccountId) => {
    const mine = generation
    const handlers: AccountHandlers = {
      onSnapshot: (snapshot, seq) => {
        if (mine !== generation || state === null) return
        set(applySnapshot(state, snapshot, seq))
        // Orders and fills missed while frames were lost come back with the newest pages.
        loadPage("orders", false)
        loadPage("fills", false)
      },
      onUpdate: (update, seq) => {
        if (mine !== generation || state === null) return
        const result = applyUpdate(state, update, seq)
        if (result.gap) {
          resubscribe(accountId)
          return
        }
        set(result.state)
      },
      onStatus: (status) => {
        if (mine !== generation || state === null) return
        set(applyStatus(state, status))
      },
      onError: (error) => {
        if (mine !== generation || state === null) return
        set(applySubscribeFailed(state, error))
      }
    }
    stop = feed.subscribe(accountId, handlers)
  }

  const resubscribe = (accountId: AccountId) => {
    stop?.()
    stop = null
    if (state !== null) set({ ...state, phase: "connecting" })
    listen(accountId)
  }

  return {
    select: (accountId) => {
      if ((state?.accountId ?? null) === accountId) return
      generation++
      stop?.()
      stop = null
      state = accountId === null ? null : initialAccountState(accountId)
      notify()
      if (accountId !== null) listen(accountId)
    },
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    loadOlderOrders: () => loadPage("orders", true),
    loadOlderFills: () => loadPage("fills", true),
    refreshHistory: () => {
      loadPage("orders", false)
      loadPage("fills", false)
    }
  }
}
